import { VIEW_WIDTH, VIEW_HEIGHT } from './events';

/**
 * Viewport camera.
 *
 * Keeps the local player centred and the view inside the map. Everything drawn
 * subtracts the camera's top-left corner, which is the same offset trick the
 * reference engine uses (`withGrid(half) - camera.x`) — expressed here as an
 * explicit rectangle because the map is larger than the screen and needs
 * clamping, which Pizza Legends does not do.
 *
 * That clamping is the deliberate difference: Drew's demo room is smaller than
 * his viewport, so his camera happily shows empty space around it. In an arena
 * that reads as a bug, so the view stops at the map's edge instead.
 */
export default class Camera {
  constructor() {
    this.x = 0; // world coords of the viewport's top-left
    this.y = 0;
    this.mapWidth = VIEW_WIDTH;
    this.mapHeight = VIEW_HEIGHT;
  }

  /** @param {{width:number,height:number,tileSize:number}} map - client map payload */
  setMap(map) {
    this.mapWidth = map.width * map.tileSize;
    this.mapHeight = map.height * map.tileSize;
  }

  /**
   * Centre on a point, then clamp.
   *
   * When the map is smaller than the viewport on an axis, centring the map
   * beats clamping to 0 — otherwise a small map sits jammed into the top-left
   * with all the dead space on one side.
   */
  follow(target) {
    if (!target) return;

    this.x = Math.round(target.x - VIEW_WIDTH / 2);
    this.y = Math.round(target.y - VIEW_HEIGHT / 2);

    this.x = this.mapWidth <= VIEW_WIDTH
      ? Math.round((this.mapWidth - VIEW_WIDTH) / 2)
      : Math.min(Math.max(this.x, 0), this.mapWidth - VIEW_WIDTH);

    this.y = this.mapHeight <= VIEW_HEIGHT
      ? Math.round((this.mapHeight - VIEW_HEIGHT) / 2)
      : Math.min(Math.max(this.y, 0), this.mapHeight - VIEW_HEIGHT);
  }

  /** World point -> screen point. Rounded: drawing on a half-pixel makes the
   *  browser interpolate, which undoes `image-rendering: pixelated`. */
  toScreenX(worldX) {
    return Math.round(worldX - this.x);
  }

  toScreenY(worldY) {
    return Math.round(worldY - this.y);
  }
}
